import { ReactNode } from 'react';
import type { FieldValues, FormState } from 'react-hook-form';
import { create } from 'zustand';

type ModalSize = 'sm' | 'md' | 'lg' | 'img' | 'qr' | 'doc' | 'p0';

type InferFormId<T extends string> =
  Lowercase<T> extends `${infer FirstWord} ${infer Rest}`
    ? `${Lowercase<FirstWord>}-${InferFormId<Rest>}`
    : `${Lowercase<T>}-form`;

export interface DialogState {
  open: boolean;
  title: string | null;
  modalTitle: string | null;
  formId: string | undefined;
  okText: string | ReactNode | null;
  component: React.ReactNode;
  cancelText?: string | ReactNode | null;
  size?: ModalSize;
  onCancel?: () => void;
  formState?: FormState<FieldValues>;
  image?: boolean;
  isSubmitting?: 'submit' | 'cancel' | null;
  okButtonDisabled?: boolean;
}

export interface DialogActions {
  onOpen: <T extends string>(config: {
    title: T;
    modalTitle: string | null;
    okText: string | ReactNode;
    component: ReactNode;
    cancelText?: string | ReactNode;
    size?: ModalSize;
    onCancel?: () => void;
    image?: boolean;
    isSubmitting?: 'submit' | 'cancel' | null;
    okButtonDisabled?: boolean;
  }) => void;
  onClose: () => void;
  setFormState: (state: DialogState['formState']) => void;
  setSubmitting: (isSubmitting: 'submit' | 'cancel' | null) => void;
}

export const useDialogFormStore = create<DialogState & DialogActions>((set, get) => ({
  open: false,
  title: null,
  formId: undefined,
  okText: null,
  modalTitle: null,
  component: <></>,
  cancelText: null,
  onCancel: () => {},
  isSubmitting: null,
  okButtonDisabled: false,
  setSubmitting: (value) => set({ isSubmitting: value }),
  setFormState: (state) => {
    set(() => ({ formState: state }));
    if (state?.isSubmitSuccessful) {
      get().onClose();
    }
  },
  onOpen: <T extends string>(config: {
    title: T;
    modalTitle?: string | null;
    okText: ReactNode;
    component: ReactNode;
    cancelText?: ReactNode;
    size?: ModalSize;
    onCancel?: () => void;
    image?: boolean;
    okButtonDisabled?: boolean;
  }) => {
    const { title, okText, component, cancelText, size, onCancel, modalTitle, image, okButtonDisabled } = config;
    const formId: InferFormId<T> | string = title
      ? ((title.toLowerCase().replace(/\s+/g, '-') + '-form') as InferFormId<T>)
      : '';
    set(() => ({
      open: true,
      title: title,
      modalTitle: modalTitle,
      formId: formId,
      component: component,
      okText: okText,
      cancelText: cancelText,
      size: size,
      onCancel: onCancel,
      image: image,
      okButtonDisabled: okButtonDisabled,
    }));
  },
  onClose: () => {
    set((state) => ({
      ...state,
      open: false,
    }));
    set((state) => ({
      ...state,
      title: null,
      formId: undefined,
      component: <></>,
      cancelText: null,
      onCancel: () => {},
      image: false,
      okButtonDisabled: false,
    }));
  },
}));
